import * as z from 'zod';

export const ListTicketsRequest = z.object({
  options: z
    .object({
      eventId: z.array(z.string()).optional(),
      offset: z.number().int().describe('Paging offset.').min(0).optional(),
      limit: z
        .number()
        .int()
        .describe('Number of items to load per page.')
        .min(0)
        .max(100)
        .optional(),
      orderNumber: z.array(z.string()).max(20).optional(),
      ticketNumber: z.array(z.string()).max(50).optional(),
      searchPhrase: z
        .string()
        .describe(
          'Textual search filter - search is performed on `"orderFullName"`, `"guestFullName"`, and `"ticketNumber"`.'
        )
        .max(200)
        .optional(),
      orderStatus: z
        .array(
          z.enum([
            'NA_ORDER_STATUS',
            'FREE',
            'PENDING',
            'PAID',
            'OFFLINE_PENDING',
            'INITIATED',
            'CANCELED',
            'DECLINED',
            'AUTHORIZED',
            'VOIDED',
            'PARTIALLY_PAID',
          ])
        )
        .max(20)
        .optional(),
      fieldset: z
        .array(z.enum(['GUEST_DETAILS', 'TICKET_DETAILS', 'GUEST_FORM']))
        .max(20)
        .optional(),
      state: z
        .array(
          z.enum([
            'ORDER_ARCHIVED',
            'ORDER_ACTIVE',
            'TICKET_ARCHIVED',
            'TICKET_ACTIVE',
            'CHECKED_IN',
            'NON_CHECKED_IN',
            'FREE',
            'PAID',
            'MEMBER',
          ])
        )
        .max(20)
        .optional(),
      memberId: z.array(z.string()).max(500).optional(),
      facet: z.array(z.string()).max(20).optional(),
      sort: z
        .string()
        .describe('Sort order. Defaults to `ticketNumber` in ascending order.')
        .max(100)
        .optional(),
      contactId: z.array(z.string()).max(100).optional(),
      ticketDefinitionId: z.array(z.string()).max(10).optional(),
      seatId: z.array(z.string()).max(50).optional(),
    })
    .describe('Options for defining the returned list of tickets.')
    .optional(),
});
export const ListTicketsResponse = z.object({
  total: z
    .number()
    .int()
    .describe('Total tickets matching the specified filters.')
    .optional(),
  offset: z.number().int().describe('Paging offset.').min(0).optional(),
  limit: z.number().int().describe('Limit.').min(0).max(100).optional(),
  tickets: z
    .array(
      z.object({
        ticketNumber: z
          .string()
          .describe(
            'Unique ticket number which is assigned automatically when creating a ticket.'
          )
          .optional(),
        orderNumber: z.string().describe('Associated order number.').optional(),
        ticketDefinitionId: z
          .string()
          .describe(
            'Ticket definition ID. See the Ticket Definition API for additional information.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        name: z.string().describe('Ticket type.').optional(),
        price: z
          .object({
            amount: z
              .string()
              .describe('*Deprecated:** Use `value` instead.')
              .optional(),
            currency: z
              .string()
              .describe(
                '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
              )
              .optional(),
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
              )
              .optional()
              .nullable(),
          })
          .describe('Ticket price.')
          .optional(),
        free: z
          .boolean()
          .describe('Whether the ticket requires payment.')
          .optional(),
        policy: z.string().describe('Ticket and event policies.').optional(),
        checkIn: z
          .object({
            created: z
              .date()
              .describe("Time of a ticket's check-in.")
              .optional()
              .nullable(),
          })
          .describe('Ticket check-in.')
          .optional(),
        orderStatus: z
          .enum([
            'NA_ORDER_STATUS',
            'FREE',
            'PENDING',
            'PAID',
            'OFFLINE_PENDING',
            'INITIATED',
            'CANCELED',
            'DECLINED',
            'AUTHORIZED',
            'VOIDED',
            'PARTIALLY_PAID',
          ])
          .describe('Associated order status.')
          .optional(),
        orderArchived: z
          .boolean()
          .describe(
            "Whether the order and ticket are archived.\nIf set to `true`, they aren't visible in the order list."
          )
          .optional(),
        orderFullName: z.string().describe('Buyer full name.').optional(),
        guestFullName: z
          .string()
          .describe('Guest full name.')
          .optional()
          .nullable(),
        guestDetails: z
          .object({
            guestAssigned: z
              .boolean()
              .describe('Whether ticket belongs to assigned guest.')
              .optional(),
            firstName: z
              .string()
              .describe('Guest first name.')
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe('Guest last name.')
              .optional()
              .nullable(),
            email: z
              .string()
              .describe('Guest email.')
              .email()
              .optional()
              .nullable(),
            form: z
              .object({
                inputValues: z
                  .array(
                    z.object({
                      inputName: z
                        .string()
                        .describe('Form field input name.')
                        .max(100)
                        .optional(),
                      value: z
                        .string()
                        .describe('Form field value.')
                        .max(5000)
                        .optional(),
                      values: z.array(z.string()).max(100).optional(),
                    })
                  )
                  .max(200)
                  .optional(),
              })
              .describe('Full form response.')
              .optional(),
            contactId: z
              .string()
              .describe('Contact ID associated with this guest.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe('Guest phone number.')
              .optional()
              .nullable(),
          })
          .describe('Guest personal details.')
          .optional(),
        archived: z
          .boolean()
          .describe('Whether the ticket is visible in an order.')
          .optional(),
        memberId: z
          .string()
          .describe('Ticket owner member ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        anonymized: z
          .boolean()
          .describe(
            'Whether ticket was anonymized by GDPR delete.\nAnonymized tickets no longer contain personally identifiable information (PII).'
          )
          .optional(),
        checkInUrl: z
          .string()
          .describe(
            'Ticket check-in URL.\n\nShown as QR code image in PDF.  <br/>\nFormat: `"https://www.wixevents.com/check-in/{ticket number},{event id}"`  <br/>\nExample: `"https://www.wixevents.com/check-in/AAAA-AAAA-BB021,00000000-0000-0000-0000-000000000000"`'
          )
          .optional(),
        ticketPdfUrl: z
          .string()
          .describe('URL for ticket PDF download.')
          .optional(),
        channel: z
          .enum(['ONLINE', 'OFFLINE_POS'])
          .describe('Associated order checkout channel type.')
          .optional(),
        walletPassUrl: z
          .string()
          .describe(
            'URL to download a ticket in `.pkpass` format for Apple Wallet.'
          )
          .url()
          .optional(),
        canceled: z
          .boolean()
          .describe('Whether ticket is canceled.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  facets: z
    .record(
      z.string(),
      z.object({
        counts: z
          .record(z.string(), z.number().int())
          .describe('Facet counts aggregated per value.')
          .optional(),
      })
    )
    .describe('Facets.')
    .optional(),
});
export const GetTicketRequest = z.object({
  ticketNumber: z.string().describe('Unique ticket number.').max(36),
  options: z
    .object({
      eventId: z
        .string()
        .describe('Event ID to which the ticket belongs.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      fieldset: z
        .array(z.enum(['GUEST_DETAILS', 'TICKET_DETAILS', 'GUEST_FORM']))
        .max(20)
        .optional(),
    })
    .describe('Options for the returned ticket data.')
    .optional(),
});
export const GetTicketResponse = z.object({
  ticketNumber: z
    .string()
    .describe(
      'Unique ticket number which is assigned automatically when creating a ticket.'
    )
    .optional(),
  orderNumber: z.string().describe('Associated order number.').optional(),
  ticketDefinitionId: z
    .string()
    .describe(
      'Ticket definition ID. See the Ticket Definition API for additional information.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
  name: z.string().describe('Ticket type.').optional(),
  price: z
    .object({
      amount: z
        .string()
        .describe('*Deprecated:** Use `value` instead.')
        .optional(),
      currency: z
        .string()
        .describe(
          '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
        )
        .optional(),
      value: z
        .string()
        .describe(
          'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
        )
        .optional()
        .nullable(),
    })
    .describe('Ticket price.')
    .optional(),
  free: z.boolean().describe('Whether the ticket requires payment.').optional(),
  policy: z.string().describe('Ticket and event policies.').optional(),
  checkIn: z
    .object({
      created: z
        .date()
        .describe("Time of a ticket's check-in.")
        .optional()
        .nullable(),
    })
    .describe('Ticket check-in.')
    .optional(),
  orderStatus: z
    .enum([
      'NA_ORDER_STATUS',
      'FREE',
      'PENDING',
      'PAID',
      'OFFLINE_PENDING',
      'INITIATED',
      'CANCELED',
      'DECLINED',
      'AUTHORIZED',
      'VOIDED',
      'PARTIALLY_PAID',
    ])
    .describe('Associated order status.')
    .optional(),
  orderArchived: z
    .boolean()
    .describe(
      "Whether the order and ticket are archived.\nIf set to `true`, they aren't visible in the order list."
    )
    .optional(),
  orderFullName: z.string().describe('Buyer full name.').optional(),
  guestFullName: z.string().describe('Guest full name.').optional().nullable(),
  guestDetails: z
    .object({
      guestAssigned: z
        .boolean()
        .describe('Whether ticket belongs to assigned guest.')
        .optional(),
      firstName: z.string().describe('Guest first name.').optional().nullable(),
      lastName: z.string().describe('Guest last name.').optional().nullable(),
      email: z.string().describe('Guest email.').email().optional().nullable(),
      form: z
        .object({
          inputValues: z
            .array(
              z.object({
                inputName: z
                  .string()
                  .describe('Form field input name.')
                  .max(100)
                  .optional(),
                value: z
                  .string()
                  .describe('Form field value.')
                  .max(5000)
                  .optional(),
                values: z.array(z.string()).max(100).optional(),
              })
            )
            .max(200)
            .optional(),
        })
        .describe('Full form response.')
        .optional(),
      contactId: z
        .string()
        .describe('Contact ID associated with this guest.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      phone: z.string().describe('Guest phone number.').optional().nullable(),
    })
    .describe('Guest personal details.')
    .optional(),
  archived: z
    .boolean()
    .describe('Whether the ticket is visible in an order.')
    .optional(),
  memberId: z
    .string()
    .describe('Ticket owner member ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  anonymized: z
    .boolean()
    .describe(
      'Whether ticket was anonymized by GDPR delete.\nAnonymized tickets no longer contain personally identifiable information (PII).'
    )
    .optional(),
  checkInUrl: z
    .string()
    .describe(
      'Ticket check-in URL.\n\nShown as QR code image in PDF.  <br/>\nFormat: `"https://www.wixevents.com/check-in/{ticket number},{event id}"`  <br/>\nExample: `"https://www.wixevents.com/check-in/AAAA-AAAA-BB021,00000000-0000-0000-0000-000000000000"`'
    )
    .optional(),
  ticketPdfUrl: z.string().describe('URL for ticket PDF download.').optional(),
  channel: z
    .enum(['ONLINE', 'OFFLINE_POS'])
    .describe('Associated order checkout channel type.')
    .optional(),
  walletPassUrl: z
    .string()
    .describe('URL to download a ticket in `.pkpass` format for Apple Wallet.')
    .url()
    .optional(),
  canceled: z
    .boolean()
    .describe('Whether ticket is canceled.')
    .optional()
    .nullable(),
});
export const CheckInTicketsRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID to which the ticket belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({ ticketNumber: z.array(z.string()).min(1).max(100).optional() })
    .describe('Options for checking in tickets.')
    .optional(),
});
export const CheckInTicketsResponse = z.object({
  tickets: z
    .array(
      z.object({
        ticketNumber: z
          .string()
          .describe(
            'Unique ticket number which is assigned automatically when creating a ticket.'
          )
          .optional(),
        orderNumber: z.string().describe('Associated order number.').optional(),
        ticketDefinitionId: z
          .string()
          .describe(
            'Ticket definition ID. See the Ticket Definition API for additional information.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        name: z.string().describe('Ticket type.').optional(),
        price: z
          .object({
            amount: z
              .string()
              .describe('*Deprecated:** Use `value` instead.')
              .optional(),
            currency: z
              .string()
              .describe(
                '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
              )
              .optional(),
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
              )
              .optional()
              .nullable(),
          })
          .describe('Ticket price.')
          .optional(),
        free: z
          .boolean()
          .describe('Whether the ticket requires payment.')
          .optional(),
        policy: z.string().describe('Ticket and event policies.').optional(),
        checkIn: z
          .object({
            created: z
              .date()
              .describe("Time of a ticket's check-in.")
              .optional()
              .nullable(),
          })
          .describe('Ticket check-in.')
          .optional(),
        orderStatus: z
          .enum([
            'NA_ORDER_STATUS',
            'FREE',
            'PENDING',
            'PAID',
            'OFFLINE_PENDING',
            'INITIATED',
            'CANCELED',
            'DECLINED',
            'AUTHORIZED',
            'VOIDED',
            'PARTIALLY_PAID',
          ])
          .describe('Associated order status.')
          .optional(),
        orderArchived: z
          .boolean()
          .describe(
            "Whether the order and ticket are archived.\nIf set to `true`, they aren't visible in the order list."
          )
          .optional(),
        orderFullName: z.string().describe('Buyer full name.').optional(),
        guestFullName: z
          .string()
          .describe('Guest full name.')
          .optional()
          .nullable(),
        guestDetails: z
          .object({
            guestAssigned: z
              .boolean()
              .describe('Whether ticket belongs to assigned guest.')
              .optional(),
            firstName: z
              .string()
              .describe('Guest first name.')
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe('Guest last name.')
              .optional()
              .nullable(),
            email: z
              .string()
              .describe('Guest email.')
              .email()
              .optional()
              .nullable(),
            form: z
              .object({
                inputValues: z
                  .array(
                    z.object({
                      inputName: z
                        .string()
                        .describe('Form field input name.')
                        .max(100)
                        .optional(),
                      value: z
                        .string()
                        .describe('Form field value.')
                        .max(5000)
                        .optional(),
                      values: z.array(z.string()).max(100).optional(),
                    })
                  )
                  .max(200)
                  .optional(),
              })
              .describe('Full form response.')
              .optional(),
            contactId: z
              .string()
              .describe('Contact ID associated with this guest.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe('Guest phone number.')
              .optional()
              .nullable(),
          })
          .describe('Guest personal details.')
          .optional(),
        archived: z
          .boolean()
          .describe('Whether the ticket is visible in an order.')
          .optional(),
        memberId: z
          .string()
          .describe('Ticket owner member ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        anonymized: z
          .boolean()
          .describe(
            'Whether ticket was anonymized by GDPR delete.\nAnonymized tickets no longer contain personally identifiable information (PII).'
          )
          .optional(),
        checkInUrl: z
          .string()
          .describe(
            'Ticket check-in URL.\n\nShown as QR code image in PDF.  <br/>\nFormat: `"https://www.wixevents.com/check-in/{ticket number},{event id}"`  <br/>\nExample: `"https://www.wixevents.com/check-in/AAAA-AAAA-BB021,00000000-0000-0000-0000-000000000000"`'
          )
          .optional(),
        ticketPdfUrl: z
          .string()
          .describe('URL for ticket PDF download.')
          .optional(),
        channel: z
          .enum(['ONLINE', 'OFFLINE_POS'])
          .describe('Associated order checkout channel type.')
          .optional(),
        walletPassUrl: z
          .string()
          .describe(
            'URL to download a ticket in `.pkpass` format for Apple Wallet.'
          )
          .url()
          .optional(),
        canceled: z
          .boolean()
          .describe('Whether ticket is canceled.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const DeleteTicketCheckInsRequest = z.object({
  eventId: z
    .string()
    .describe('Event ID to which the ticket belongs.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({ ticketNumber: z.array(z.string()).min(1).max(100).optional() })
    .describe('Options for deleting ticket check-ins.')
    .optional(),
});
export const DeleteTicketCheckInsResponse = z.object({
  tickets: z
    .array(
      z.object({
        ticketNumber: z
          .string()
          .describe(
            'Unique ticket number which is assigned automatically when creating a ticket.'
          )
          .optional(),
        orderNumber: z.string().describe('Associated order number.').optional(),
        ticketDefinitionId: z
          .string()
          .describe(
            'Ticket definition ID. See the Ticket Definition API for additional information.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        name: z.string().describe('Ticket type.').optional(),
        price: z
          .object({
            amount: z
              .string()
              .describe('*Deprecated:** Use `value` instead.')
              .optional(),
            currency: z
              .string()
              .describe(
                '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
              )
              .optional(),
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
              )
              .optional()
              .nullable(),
          })
          .describe('Ticket price.')
          .optional(),
        free: z
          .boolean()
          .describe('Whether the ticket requires payment.')
          .optional(),
        policy: z.string().describe('Ticket and event policies.').optional(),
        checkIn: z
          .object({
            created: z
              .date()
              .describe("Time of a ticket's check-in.")
              .optional()
              .nullable(),
          })
          .describe('Ticket check-in.')
          .optional(),
        orderStatus: z
          .enum([
            'NA_ORDER_STATUS',
            'FREE',
            'PENDING',
            'PAID',
            'OFFLINE_PENDING',
            'INITIATED',
            'CANCELED',
            'DECLINED',
            'AUTHORIZED',
            'VOIDED',
            'PARTIALLY_PAID',
          ])
          .describe('Associated order status.')
          .optional(),
        orderArchived: z
          .boolean()
          .describe(
            "Whether the order and ticket are archived.\nIf set to `true`, they aren't visible in the order list."
          )
          .optional(),
        orderFullName: z.string().describe('Buyer full name.').optional(),
        guestFullName: z
          .string()
          .describe('Guest full name.')
          .optional()
          .nullable(),
        guestDetails: z
          .object({
            guestAssigned: z
              .boolean()
              .describe('Whether ticket belongs to assigned guest.')
              .optional(),
            firstName: z
              .string()
              .describe('Guest first name.')
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe('Guest last name.')
              .optional()
              .nullable(),
            email: z
              .string()
              .describe('Guest email.')
              .email()
              .optional()
              .nullable(),
            form: z
              .object({
                inputValues: z
                  .array(
                    z.object({
                      inputName: z
                        .string()
                        .describe('Form field input name.')
                        .max(100)
                        .optional(),
                      value: z
                        .string()
                        .describe('Form field value.')
                        .max(5000)
                        .optional(),
                      values: z.array(z.string()).max(100).optional(),
                    })
                  )
                  .max(200)
                  .optional(),
              })
              .describe('Full form response.')
              .optional(),
            contactId: z
              .string()
              .describe('Contact ID associated with this guest.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe('Guest phone number.')
              .optional()
              .nullable(),
          })
          .describe('Guest personal details.')
          .optional(),
        archived: z
          .boolean()
          .describe('Whether the ticket is visible in an order.')
          .optional(),
        memberId: z
          .string()
          .describe('Ticket owner member ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        anonymized: z
          .boolean()
          .describe(
            'Whether ticket was anonymized by GDPR delete.\nAnonymized tickets no longer contain personally identifiable information (PII).'
          )
          .optional(),
        checkInUrl: z
          .string()
          .describe(
            'Ticket check-in URL.\n\nShown as QR code image in PDF.  <br/>\nFormat: `"https://www.wixevents.com/check-in/{ticket number},{event id}"`  <br/>\nExample: `"https://www.wixevents.com/check-in/AAAA-AAAA-BB021,00000000-0000-0000-0000-000000000000"`'
          )
          .optional(),
        ticketPdfUrl: z
          .string()
          .describe('URL for ticket PDF download.')
          .optional(),
        channel: z
          .enum(['ONLINE', 'OFFLINE_POS'])
          .describe('Associated order checkout channel type.')
          .optional(),
        walletPassUrl: z
          .string()
          .describe(
            'URL to download a ticket in `.pkpass` format for Apple Wallet.'
          )
          .url()
          .optional(),
        canceled: z
          .boolean()
          .describe('Whether ticket is canceled.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
export const UpdateTicketRequest = z.object({
  ticketNumber: z.string().describe('Unique ticket number.').min(1).max(36),
  options: z
    .object({
      eventId: z
        .string()
        .describe('Event ID to which the ticket belongs.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      fields: z
        .object({ paths: z.array(z.string()) })
        .describe(
          'Set of field paths, specifying which parts of this resource to update.\nWhen `fields` is empty, the request is interpreted as a full update.\nBehavior follows [google.protobuf.FieldMask](https://developers.google.com/protocol-buffers/docs/reference/google.protobuf#google.protobuf.FieldMask) semantics.'
        )
        .optional()
        .nullable(),
      archived: z
        .boolean()
        .describe('Whether to archive the ticket.')
        .optional(),
      guestDetails: z
        .object({
          form: z
            .object({
              inputValues: z
                .array(
                  z.object({
                    inputName: z
                      .string()
                      .describe('Form field input name.')
                      .max(100)
                      .optional(),
                    value: z
                      .string()
                      .describe('Form field value.')
                      .max(5000)
                      .optional(),
                    values: z.array(z.string()).max(100).optional(),
                  })
                )
                .max(200)
                .optional(),
            })
            .describe(
              "Form of a guest assigned to a ticket.\n\nTo update the form of a guest not assigned to a ticket, call Update Order.\n\nDoesn't support partial updates, the entire form object will be replaced when `guestDetails.form` is masked."
            )
            .optional(),
        })
        .describe('Assigned guest details.')
        .optional(),
    })
    .describe('Ticket details to update.')
    .optional(),
});
export const UpdateTicketResponse = z.object({
  ticket: z
    .object({
      ticketNumber: z
        .string()
        .describe(
          'Unique ticket number which is assigned automatically when creating a ticket.'
        )
        .optional(),
      orderNumber: z.string().describe('Associated order number.').optional(),
      ticketDefinitionId: z
        .string()
        .describe(
          'Ticket definition ID. See the Ticket Definition API for additional information.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      name: z.string().describe('Ticket type.').optional(),
      price: z
        .object({
          amount: z
            .string()
            .describe('*Deprecated:** Use `value` instead.')
            .optional(),
          currency: z
            .string()
            .describe(
              '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
            )
            .optional(),
          value: z
            .string()
            .describe(
              'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
            )
            .optional()
            .nullable(),
        })
        .describe('Ticket price.')
        .optional(),
      free: z
        .boolean()
        .describe('Whether the ticket requires payment.')
        .optional(),
      policy: z.string().describe('Ticket and event policies.').optional(),
      checkIn: z
        .object({
          created: z
            .date()
            .describe("Time of a ticket's check-in.")
            .optional()
            .nullable(),
        })
        .describe('Ticket check-in.')
        .optional(),
      orderStatus: z
        .enum([
          'NA_ORDER_STATUS',
          'FREE',
          'PENDING',
          'PAID',
          'OFFLINE_PENDING',
          'INITIATED',
          'CANCELED',
          'DECLINED',
          'AUTHORIZED',
          'VOIDED',
          'PARTIALLY_PAID',
        ])
        .describe('Associated order status.')
        .optional(),
      orderArchived: z
        .boolean()
        .describe(
          "Whether the order and ticket are archived.\nIf set to `true`, they aren't visible in the order list."
        )
        .optional(),
      orderFullName: z.string().describe('Buyer full name.').optional(),
      guestFullName: z
        .string()
        .describe('Guest full name.')
        .optional()
        .nullable(),
      guestDetails: z
        .object({
          guestAssigned: z
            .boolean()
            .describe('Whether ticket belongs to assigned guest.')
            .optional(),
          firstName: z
            .string()
            .describe('Guest first name.')
            .optional()
            .nullable(),
          lastName: z
            .string()
            .describe('Guest last name.')
            .optional()
            .nullable(),
          email: z
            .string()
            .describe('Guest email.')
            .email()
            .optional()
            .nullable(),
          form: z
            .object({
              inputValues: z
                .array(
                  z.object({
                    inputName: z
                      .string()
                      .describe('Form field input name.')
                      .max(100)
                      .optional(),
                    value: z
                      .string()
                      .describe('Form field value.')
                      .max(5000)
                      .optional(),
                    values: z.array(z.string()).max(100).optional(),
                  })
                )
                .max(200)
                .optional(),
            })
            .describe('Full form response.')
            .optional(),
          contactId: z
            .string()
            .describe('Contact ID associated with this guest.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          phone: z
            .string()
            .describe('Guest phone number.')
            .optional()
            .nullable(),
        })
        .describe('Guest personal details.')
        .optional(),
      archived: z
        .boolean()
        .describe('Whether the ticket is visible in an order.')
        .optional(),
      memberId: z
        .string()
        .describe('Ticket owner member ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      anonymized: z
        .boolean()
        .describe(
          'Whether ticket was anonymized by GDPR delete.\nAnonymized tickets no longer contain personally identifiable information (PII).'
        )
        .optional(),
      checkInUrl: z
        .string()
        .describe(
          'Ticket check-in URL.\n\nShown as QR code image in PDF.  <br/>\nFormat: `"https://www.wixevents.com/check-in/{ticket number},{event id}"`  <br/>\nExample: `"https://www.wixevents.com/check-in/AAAA-AAAA-BB021,00000000-0000-0000-0000-000000000000"`'
        )
        .optional(),
      ticketPdfUrl: z
        .string()
        .describe('URL for ticket PDF download.')
        .optional(),
      channel: z
        .enum(['ONLINE', 'OFFLINE_POS'])
        .describe('Associated order checkout channel type.')
        .optional(),
      walletPassUrl: z
        .string()
        .describe(
          'URL to download a ticket in `.pkpass` format for Apple Wallet.'
        )
        .url()
        .optional(),
      canceled: z
        .boolean()
        .describe('Whether ticket is canceled.')
        .optional()
        .nullable(),
    })
    .describe('Updated ticket.')
    .optional(),
});
export const BulkUpdateTicketsRequest = z.object({
  options: z
    .object({
      eventId: z
        .string()
        .describe('Event ID to which the tickets belong.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      ticketNumber: z.array(z.string()).min(1).max(100).optional(),
      archived: z
        .boolean()
        .describe('Whether tickets are archived.')
        .optional(),
    })
    .describe('Options for updating the tickets.')
    .optional(),
});
export const BulkUpdateTicketsResponse = z.object({
  tickets: z
    .array(
      z.object({
        ticketNumber: z
          .string()
          .describe(
            'Unique ticket number which is assigned automatically when creating a ticket.'
          )
          .optional(),
        orderNumber: z.string().describe('Associated order number.').optional(),
        ticketDefinitionId: z
          .string()
          .describe(
            'Ticket definition ID. See the Ticket Definition API for additional information.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        name: z.string().describe('Ticket type.').optional(),
        price: z
          .object({
            amount: z
              .string()
              .describe('*Deprecated:** Use `value` instead.')
              .optional(),
            currency: z
              .string()
              .describe(
                '3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.'
              )
              .optional(),
            value: z
              .string()
              .describe(
                'Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.'
              )
              .optional()
              .nullable(),
          })
          .describe('Ticket price.')
          .optional(),
        free: z
          .boolean()
          .describe('Whether the ticket requires payment.')
          .optional(),
        policy: z.string().describe('Ticket and event policies.').optional(),
        checkIn: z
          .object({
            created: z
              .date()
              .describe("Time of a ticket's check-in.")
              .optional()
              .nullable(),
          })
          .describe('Ticket check-in.')
          .optional(),
        orderStatus: z
          .enum([
            'NA_ORDER_STATUS',
            'FREE',
            'PENDING',
            'PAID',
            'OFFLINE_PENDING',
            'INITIATED',
            'CANCELED',
            'DECLINED',
            'AUTHORIZED',
            'VOIDED',
            'PARTIALLY_PAID',
          ])
          .describe('Associated order status.')
          .optional(),
        orderArchived: z
          .boolean()
          .describe(
            "Whether the order and ticket are archived.\nIf set to `true`, they aren't visible in the order list."
          )
          .optional(),
        orderFullName: z.string().describe('Buyer full name.').optional(),
        guestFullName: z
          .string()
          .describe('Guest full name.')
          .optional()
          .nullable(),
        guestDetails: z
          .object({
            guestAssigned: z
              .boolean()
              .describe('Whether ticket belongs to assigned guest.')
              .optional(),
            firstName: z
              .string()
              .describe('Guest first name.')
              .optional()
              .nullable(),
            lastName: z
              .string()
              .describe('Guest last name.')
              .optional()
              .nullable(),
            email: z
              .string()
              .describe('Guest email.')
              .email()
              .optional()
              .nullable(),
            form: z
              .object({
                inputValues: z
                  .array(
                    z.object({
                      inputName: z
                        .string()
                        .describe('Form field input name.')
                        .max(100)
                        .optional(),
                      value: z
                        .string()
                        .describe('Form field value.')
                        .max(5000)
                        .optional(),
                      values: z.array(z.string()).max(100).optional(),
                    })
                  )
                  .max(200)
                  .optional(),
              })
              .describe('Full form response.')
              .optional(),
            contactId: z
              .string()
              .describe('Contact ID associated with this guest.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            phone: z
              .string()
              .describe('Guest phone number.')
              .optional()
              .nullable(),
          })
          .describe('Guest personal details.')
          .optional(),
        archived: z
          .boolean()
          .describe('Whether the ticket is visible in an order.')
          .optional(),
        memberId: z
          .string()
          .describe('Ticket owner member ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        anonymized: z
          .boolean()
          .describe(
            'Whether ticket was anonymized by GDPR delete.\nAnonymized tickets no longer contain personally identifiable information (PII).'
          )
          .optional(),
        checkInUrl: z
          .string()
          .describe(
            'Ticket check-in URL.\n\nShown as QR code image in PDF.  <br/>\nFormat: `"https://www.wixevents.com/check-in/{ticket number},{event id}"`  <br/>\nExample: `"https://www.wixevents.com/check-in/AAAA-AAAA-BB021,00000000-0000-0000-0000-000000000000"`'
          )
          .optional(),
        ticketPdfUrl: z
          .string()
          .describe('URL for ticket PDF download.')
          .optional(),
        channel: z
          .enum(['ONLINE', 'OFFLINE_POS'])
          .describe('Associated order checkout channel type.')
          .optional(),
        walletPassUrl: z
          .string()
          .describe(
            'URL to download a ticket in `.pkpass` format for Apple Wallet.'
          )
          .url()
          .optional(),
        canceled: z
          .boolean()
          .describe('Whether ticket is canceled.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
